import {
    Directive,
    ElementRef,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    Renderer2,
    Self,
    ViewContainerRef,
} from '@angular/core';
import { FormGroupDirective, NgControl } from '@angular/forms';
import { combineLatest, EMPTY, map, merge, startWith, Subject, Subscription } from 'rxjs';
import { ControlErrorContainerDirective } from './control-error-container.directive';
import { ControlErrorComponent } from './control-error.component';
import { FormSubmitDirective } from './form-submit.directive';

/* eslint-disable @angular-eslint/directive-selector */
@Directive({
    standalone: true,
    selector:
        '[formControl][formControlErrorMessages], [formControlName][formControlErrorMessages], [formGroup][formControlErrorMessages], [formGroupName][formControlErrorMessages]',
})
export class FormControlErrorMessagesDirective implements OnInit, OnDestroy {
    @Input() public formControlErrorMessages: Record<string, string> | null = {};

    private readonly errorTargetContainerReference =
        this.controlErrorContainer?.viewContainerReference ?? this.viewContainer;
    private readonly errorComponentReference =
        this.errorTargetContainerReference.createComponent(ControlErrorComponent);
    private readonly blurred = new Subject<void>();
    private subscription?: Subscription;

    public constructor(
        private readonly viewContainer: ViewContainerRef,
        private readonly host: ElementRef,
        private readonly renderer: Renderer2,
        @Self() @Optional() private readonly control?: NgControl,
        @Self() @Optional() private readonly formGroup?: FormGroupDirective,
        @Optional() private readonly controlErrorContainer?: ControlErrorContainerDirective,
        // We can inject an instance of the FormSubmitDirective
        // that is attached to the nearest parent of this control
        @Optional() private readonly form?: FormSubmitDirective,
    ) {
        if (this.control == undefined && this.formGroup == undefined) {
            throw new Error('No form control or group found');
        }

        // This creates space for the errors message to avoid the UI jumping around.
        if (this.controlErrorContainer == undefined) {
            this.renderer.setStyle(this.host.nativeElement, 'margin-block-end', '1.25rem');
        }
    }

    private set errorText(text: string | undefined) {
        this.errorComponentReference.instance.errorText = text;
    }

    @HostListener('focusout', ['$event'])
    public inputBlurListener($event: FocusEvent) {
        // If the focus change was between elements within this component
        // don't emit the blur event
        const focusChangeWithinComponent =
            $event.relatedTarget instanceof Node &&
            $event.currentTarget instanceof Node &&
            $event.currentTarget.contains($event.relatedTarget);
        if (focusChangeWithinComponent) return;

        this.blurred.next();
    }

    public ngOnInit(): void {
        // Observable provided via the injected instance of the form by the FormSubmitDirective
        const submit$ = (this.form?.submit$ ?? EMPTY).pipe(
            map(() => ({ formSubmitAttempted: true })),
            startWith({ formSubmitAttempted: false }),
        );
        const valueChange$ = merge(
            this.control?.valueChanges ?? EMPTY,
            this.formGroup?.valueChanges ?? EMPTY,
        ).pipe(
            map(() => ({ valueChanged: true })),
            startWith({ valueChanged: false }),
        );
        const blurred$ = this.blurred.pipe(
            map(() => ({ blurred: true })),
            startWith({ blurred: false }),
        );
        const showError$ = combineLatest([submit$, valueChange$, blurred$]).pipe(
            map(([{ formSubmitAttempted }, { valueChanged }, { blurred }]) => ({
                formSubmitAttempted,
                valueChanged,
                blurred,
            })),
        );

        this.subscription = showError$.subscribe((event) => this.handleFormChange(event));
    }

    public ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }

    private handleFormChange(event: FormChangeEvent) {
        const { blurred, formSubmitAttempted, valueChanged } = event;
        if (!blurred && !formSubmitAttempted && !valueChanged) return;
        const controlUntouched = this.control?.untouched ?? true;
        const formGroupUntouched = this.formGroup?.untouched ?? true;
        if (!formSubmitAttempted && controlUntouched && formGroupUntouched) {
            return;
        }

        const errors = this.control?.errors ?? this.formGroup?.errors;
        if (errors) {
            const firstErrorKey = Object.keys(errors)[0];
            this.errorText = this.formControlErrorMessages?.[firstErrorKey] ?? 'Invalid value';
            return;
        }

        this.errorText = undefined;
    }
}

type FormChangeEvent = {
    blurred: boolean;
    valueChanged: boolean;
    formSubmitAttempted: boolean;
};
